import {
  faCircleNotch,
  faUpRightAndDownLeftFromCenter,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { orderBy } from 'lodash'
import moment from 'moment'
import { classNames } from 'primereact/utils'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  closeMinimizedEmailWindow,
  selectMinizedEmailWindows,
} from '../../features/projectManagement/projectManagementSlice'
import HandlerAssignmentNotificationEmailEditorDialog, {
  HandlerAssignmentNotificationEmailEditorDialogRef,
} from '../../pages/project-management-pmr/shared/components/handler-assignment-notification-email-editor-dialog/HandlerAssignmentNotificationEmailEditorDialog'
import UnsavedChangesWarningDialog from '../../pages/project-management-pmr/shared/components/handler-assignment-notification-email-editor-dialog/unsaved-changes-warning-dialog/UnsavedChangesWarningDialog'
import { useDiscardDraft } from '../../pages/project-management-pmr/shared/components/handler-assignment-notification-email-editor-dialog/useDiscardDraft'
import { useHandlerAssignmentNotificationEmailEditor } from '../../pages/project-management-pmr/shared/components/handler-assignment-notification-email-editor-dialog/useHandlerAssignmentNotificationEmailEditor'
import { useSaveHandler } from '../../pages/project-management-pmr/shared/hooks/useSaveHandler'
import ProjectService from '../../services/ProjectService'
import { HandlerAssignmentMailDraftModel } from '../../shared/models/service-models/HandlerAssignmentMailDraft.model'

type Props = {}

const MinimizedHandlerAssignmentEmailWindows = (props: Props) => {
  const emailWindows = useSelector(selectMinizedEmailWindows)
  const top4Windows = orderBy(emailWindows, w => moment(w.createdAt).toDate(), 'desc').splice(0, 4)
  const dispatch = useDispatch()
  const [activeDraft, setActiveDraft] = useState<HandlerAssignmentMailDraftModel>()
  const [minimizedWindowIdToClose, setMinimizedWindowIdToClose] = useState<string>()
  const emailEditorRef = useRef<HandlerAssignmentNotificationEmailEditorDialogRef | null>(null)
  const {
    emailEditorVisible,
    openEmailEditor,
    closeEmailEditor,
    emailEditorOrderlines,
    setEmailEditorVisible,
  } = useHandlerAssignmentNotificationEmailEditor({
    emailEditorRef,
  })
  const { discardDraft } = useDiscardDraft()
  const { isSavingHandler, saveHandler } = useSaveHandler({
    onSuccess: () => {
      closeEmailEditor()
      handleClose(activeDraft!.id)
      discardDraft(activeDraft!.id)
    },
  })
  const [maximizingWindowId, setMaximizingWindowId] = useState<string>()
  const [unsavedChangedWarningDialogVisible, setUnsavedChangedWarningDialogVisible] =
    useState<boolean>(false)

  // update session storage
  useEffect(() => {
    sessionStorage.setItem('minimizedEmailWindows', JSON.stringify(emailWindows))
  }, [emailWindows])

  const handleClose = (id: string) => {
    dispatch(closeMinimizedEmailWindow({ id }))
  }

  const handleMaximize = async (id: string) => {
    setMaximizingWindowId(id)
    const draft = await ProjectService.getHandlerAssignmentMailDraft(id, true)
    setActiveDraft(draft.data)
    openEmailEditor(
      draft.data.draftProjectLines.map(dpl => dpl.orderlineDetail),
      draft.data.newHandlerInformation,
      draft.data.mail
    )
    setMaximizingWindowId(undefined)
  }

  return (
    <>
      <div className="absolute right-0 bottom-0 flex gap-1 flex-row-reverse">
        {top4Windows.map(window => {
          const title = window.subject || '[Draft]'

          return (
            <div
              key={window.id}
              className={classNames(
                'min-h-[2rem] text-white flex items-center p-[1rem] rounded-sm font-semibold gap-[1rem] w-[22rem] justify-between',
                !!maximizingWindowId ? 'pointer-events-none bg-blue-300' : 'bg-blue-500'
              )}
            >
              <label title={title} className="truncate">
                {title}
              </label>

              <div className="flex flex-1 items-center gap-3 justify-end">
                <button className="[all:unset]" onClick={() => handleMaximize(window.id)}>
                  <FontAwesomeIcon
                    size={'sm'}
                    icon={
                      window.id === maximizingWindowId
                        ? faCircleNotch
                        : faUpRightAndDownLeftFromCenter
                    }
                    spin={window.id === maximizingWindowId}
                  />
                </button>

                <button
                  className="[all:unset]"
                  onClick={() => {
                    setUnsavedChangedWarningDialogVisible(true)
                    setMinimizedWindowIdToClose(window.id)
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} size={'lg'} />
                </button>
              </div>
            </div>
          )
        })}
      </div>
      <HandlerAssignmentNotificationEmailEditorDialog
        visible={emailEditorVisible}
        onHide={() => {
          closeEmailEditor()
          handleClose(activeDraft!.id)
        }}
        selectedNewHandler={activeDraft?.newHandlerInformation}
        orderlines={emailEditorOrderlines}
        onSend={mail =>
          saveHandler(
            activeDraft!.draftProjectLines.map(dpl => dpl.orderlineDetail),
            activeDraft!.newHandlerInformation!.handlerType,
            activeDraft!.newHandlerInformation,
            mail
          )
        }
        ref={emailEditorRef}
        onMinimize={() => {
          setEmailEditorVisible(false)
        }}
        isSending={isSavingHandler}
        handlerType={activeDraft?.newHandlerInformation?.handlerType!}
      />
      <UnsavedChangesWarningDialog
        onConfirm={() => {
          handleClose(minimizedWindowIdToClose!)
          setUnsavedChangedWarningDialogVisible(false)
        }}
        onHide={() => setUnsavedChangedWarningDialogVisible(false)}
        visible={unsavedChangedWarningDialogVisible}
      />
    </>
  )
}

export default MinimizedHandlerAssignmentEmailWindows
